import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { Button, ButtonText, Cell, Empty, H4, Icon, Input, ModalContent, ModalHeader, ModalLayout, ModalTitle, Row, Table, TableBody, Text, Tooltip, User } from 'components';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { theme, toRem } from 'design';
import { clearRequest, selectAPIRequestStatus } from 'store/ApiClient';
import { Employees, getEmployeesRequest, selectEmployees } from 'store/Collections';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeType, IApiQueryParam, IEmployee, IUploadPayment, RequestStatus } from 'types';
import { centsToDollarsDisplay, formatJobTitle } from 'utils';

interface IUploadPaymentMatchProps {
  payment: IUploadPayment;
  siteId: string;
  onClose: () => void;
  onUpdatePayment: (payment: IUploadPayment) => void;
  isEmployeeDisabled: (emp: IEmployee) => string;
}

const PaymentHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  border-radius: ${toRem(theme.borderRadius.small)};
  width: 100%;
  background-color: ${theme.color.neutral[100]};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.lg)};
  margin-bottom: ${toRem(theme.space['2xl'])};
  align-items: center;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  align-items: center;

  .icon {
    margin-right: ${toRem(theme.space.lg)};
  }

  .match-name {
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

const EmployeeSelectContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  width: 100%;

  div.no-radius {
    border-radius: 0;
  }
`;

const EmployeeSelectorHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  border-radius: ${toRem(theme.borderRadius.small)} ${toRem(theme.borderRadius.small)} 0 0;
  width: 100%;
  background-color: ${theme.color.neutral[100]};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
  justify-content: space-between;
  align-items: center;
`;

const StyledTable = styled(Table)`
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-top-width: 0;
`;

const StyledRow = styled(Row)`
  @media (min-width: ${theme.breakpoint.small}px) {
    .btn-select-employee {
      visibility: hidden;
    }

    &:hover {
      .btn-select-employee {
        visibility: visible;
      }
    }
  }
`;

const SelectUserButton = styled(ButtonText)`
  color: ${theme.color.primary[500]};
  margin-left: ${toRem(theme.space.sm)};
  /* off-scale exception: no font-size token below xxs (11px) */
  font-size: ${toRem(10)};
`;

const SearchInput = styled(Input)`
  height: ${toRem(32)};
  max-width: ${toRem(160)};
`;

const NoResults = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  justify-content: center;
  align-items: center;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-top: 0;
`;

const UploadPaymentMatch = ({ payment, onUpdatePayment, isEmployeeDisabled, siteId, onClose }: IUploadPaymentMatchProps) => {
  const dispatch = useDispatch();
  const { data: employees } = useSelector(selectEmployees()) || { data: [], total: null };
  const [isSelectAll, setIsSelectAll] = useState<boolean>(!(payment.employeeMatch && payment.employeeMatch.length > 1));
  const fetchEmployeeStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employees.GET_EMPLOYEES_REQUEST));
  const [isEmployeesLoaded, setIsEmployeesLoaded] = useState<boolean>(false);

  const [query, setQuery] = useState<any>([]);

  const onNameChange = (value: string) => {
    if (value.trim() === '') {
      setQuery([]);
    } else {
      setQuery([
        {
          key: 'name',
          operator: ApiQueryOperator.like,
          value,
        },
      ]);
    }
  };

  const debounceMs = 500;
  const delayedNameChange = debounce(onNameChange, debounceMs);

  const onSelectEmployee = (employee: IEmployee) => {
    onUpdatePayment({ ...payment, employee });
  };

  const fetchEmployees = () => {
    const q = Object.values(query || []).filter((q: any) => q.key !== 'status') as IApiQueryParam[];
    const options: IApiQueryOptions = {
      sort: 'name',
      limit: 5,
      q,
    };
    dispatch(getEmployeesRequest(AffiliationType.SITES, siteId, options));
  };

  const cbFetchEmployees = useCallback(fetchEmployees, [query]);

  useEffect(() => {
    if (isSelectAll === true) {
      setIsEmployeesLoaded(false);
      cbFetchEmployees();
    } else {
      dispatch(clearRequest(Employees.GET_EMPLOYEES_REQUEST));
    }
  }, [isSelectAll, cbFetchEmployees, dispatch]);

  useEffect(() => {
    if (isSelectAll === true && fetchEmployeeStatus === RequestStatus.SUCCESS) {
      setIsEmployeesLoaded(true);
    }
  }, [isSelectAll, fetchEmployeeStatus]);

  const loadedEmployees = isEmployeesLoaded ? employees : [];

  const userRow = (employee: IEmployee) => {
    const disabledReason = isEmployeeDisabled(employee);
    return (
      <StyledRow key={`employee_${employee.id}`}>
        <Cell>
          <User
            className="employee-avatar"
            stackable={false}
            id={employee.id}
            username={employee.name}
            image={employee.user?.avatarUrl}
            hasPaymentMethod={!!employee.user?.card}
            isLoading={false}
            role={formatJobTitle(employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : employee.sites[0]?.job.title)}
          />
        </Cell>
        <Cell>{employee.sites[0].employeeId || '--'}</Cell>
        <Cell>
          <Button size="small" appearance="primary" label="Select" disabled={!!disabledReason} tooltipId={`employee-tooltip-${employee.id}`} className="btn-select-employee" onClick={() => onSelectEmployee(employee)} />
          {disabledReason && (
            <Tooltip id={`employee-tooltip-${employee.id}`} place="left">
              {disabledReason}
            </Tooltip>
          )}
        </Cell>
      </StyledRow>
    );
  };

  return (
    <ModalLayout maxWidth={540} isActive onClose={onClose}>
      <ModalHeader>
        <ModalTitle>Select Match</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {!payment.employee && (
          <PaymentHeader>
            <StatusContainer>
              <Icon icon="Error" fill={payment.employeeMatch && payment.employeeMatch.length > 1 ? theme.color.warning[50] : theme.color.danger[50]} className="icon" />
              <H4 fontWeight="bold" className="match-name">
                {payment.employeeId || payment.name}
              </H4>
            </StatusContainer>
            <H4 fontWeight="bold">{centsToDollarsDisplay(payment.amount)}</H4>
          </PaymentHeader>
        )}

        {payment.employee && (
          <PaymentHeader>
            <StatusContainer>
              <User
                className="employee-avatar"
                stackable={false}
                id={payment.employee.id}
                username={payment.employee.name}
                image={payment.employee.user?.avatarUrl}
                hasPaymentMethod={!!payment.employee.user?.card}
                isLoading={false}
                role={formatJobTitle(payment.employee.type === EmployeeType.ADMIN ? DEFAULT_ORG_ADMIN_JOB_TITLE : payment.employee.sites[0]?.job.title)}
              />
            </StatusContainer>
            <H4 fontWeight="bold">{centsToDollarsDisplay(payment.amount)}</H4>
          </PaymentHeader>
        )}

        <EmployeeSelectContainer>
          {!isSelectAll && payment.employeeMatch && payment.employeeMatch?.length > 0 && (
            <>
              <EmployeeSelectorHeader>
                <Text size="xxsmall" fontWeight="bold">
                  {`${payment.employeeMatch?.length} match${payment.employeeMatch?.length > 1 && 'es'} found`}
                </Text>
                <SelectUserButton label="Select another user" onClick={() => setIsSelectAll(true)} />
              </EmployeeSelectorHeader>
              <StyledTable scrollable className="no-radius">
                <TableBody>{payment.employeeMatch?.map((employee: IEmployee) => userRow(employee))}</TableBody>
              </StyledTable>
            </>
          )}

          {isSelectAll && (
            <>
              <EmployeeSelectorHeader>
                <Text size="xxsmall" fontWeight="bold">
                  Please select a match
                </Text>
                <SearchInput autoFocus onChange={(e) => delayedNameChange(e.target.value)} placeholder="Search by name" tabIndex={1} />
              </EmployeeSelectorHeader>
              {isEmployeesLoaded && !isEmpty(loadedEmployees) && (
                <StyledTable scrollable className="no-radius">
                  <TableBody>{loadedEmployees?.map((employee: IEmployee) => userRow(employee))}</TableBody>
                </StyledTable>
              )}
              {isEmployeesLoaded && fetchEmployeeStatus === RequestStatus.SUCCESS && isEmpty(loadedEmployees) && (
                <NoResults>
                  <Empty icon="People" message="No employees matching name found" data-cy="empty-icon" />
                </NoResults>
              )}
            </>
          )}
        </EmployeeSelectContainer>
      </ModalContent>
    </ModalLayout>
  );
};

export default UploadPaymentMatch;
